'use client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { SITE } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { Link } from '@/i18n/routing';
import { listCases } from '../../../../apis';
import type { ClawV1CaseInfo } from '../../../../types';
import { CaseDialog } from '../../../Case/CaseDialog';

const CASE_PAGE_SIZE = 3;

interface WelcomeQualityCasesProps {
  /** 点击 case 时回调：用于清空当前输入草稿（同步取消 SkillSceneCard 高亮）。 */
  onContentClick?: () => void;
}

export const WelcomeQualityCases = ({ onContentClick }: WelcomeQualityCasesProps) => {
  const t = useTranslations();
  const [selectedCase, setSelectedCase] = useState<ClawV1CaseInfo>();
  const [openCaseDialog, setOpenCaseDialog] = useState(false);

  const { data } = useFetch(() => listCases({ 'pager.page': 0, 'pager.pagesz': CASE_PAGE_SIZE }));
  const qualityCases = data?.data?.cases ?? [];
  if (qualityCases.length === 0) return null;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-sm text-system-white36">
        <div>{t('claw.chat.welcome.case')}</div>
        <Link href="/claw/cases" className="transition-colors hover:text-system-white">
          {t('claw.chat.welcome.caseMore')}
        </Link>
      </div>

      <div className="mt-3 flex w-full items-start gap-4">
        {qualityCases.map((item, index) => {
          const hasValidSize = (item.video_width ?? 0) > 0 && (item.video_height ?? 0) > 0;
          const width = hasValidSize ? (item.video_width ?? 16) : 16;
          const height = hasValidSize ? (item.video_height ?? 9) : 9;
          const shouldShowWatermark = SITE === 'china' && !!item.cover_url;

          return (
            <button
              type="button"
              key={item.id ?? item.session_id ?? `case-${index}`}
              className="relative aspect-video min-w-0 flex-1 cursor-pointer overflow-hidden rounded-12 border border-system-white08 bg-system-white04 text-left"
              onClick={() => {
                onContentClick?.();
                reportEvent('claw_element_click', {
                  element_name: 'claw_case_dialog_open',
                  from: 'claw_welcome',
                  case_id: item.id || '',
                  case_name: item.name || '',
                });
                setSelectedCase(item);
                setOpenCaseDialog(true);
                reportEvent('claw_element_click', {
                  element_name: 'case_content_click',
                  case_name: item.name ?? '',
                });
              }}
            >
              <div className="relative h-full w-full overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0)_100%)]">
                {item.cover_url ? (
                  <img
                    src={item.cover_url}
                    alt={item.name ?? ''}
                    className="pointer-events-none size-full object-cover"
                  />
                ) : null}
                {shouldShowWatermark && <MediaWithWatermark overlay width={width} height={height} showWatermark />}
              </div>
            </button>
          );
        })}
      </div>
      <CaseDialog open={openCaseDialog} onClose={() => setOpenCaseDialog(false)} caseInfo={selectedCase} />
    </div>
  );
};
